import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ProseChange, RemediationReviewItem, ReviewDecision } from '@libs/types';

import { RemediationDecision } from '@libs/constants';

import type { AnyChange } from '../components/ReviewChangeRail';
import { nextChangeAfterDecision } from '../utils/review-cursor';

/**
 * Whether a keystroke belongs to something the user is typing into or choosing
 * from, rather than to the review shortcuts.
 *
 * A tag-name test on INPUT/TEXTAREA was not enough. A MUI Select, Menu or
 * Autocomplete puts focus on a `div` with a listbox role and does its own
 * type-ahead, so pressing `a` inside an open dropdown decided the change behind
 * it instead of jumping to the options starting with A — and the dropdown sat
 * over the document, so the reviewer could not see what they had just done.
 * Dialogs are excluded for the same reason: a modal is a different task, and its
 * own controls own the keyboard while it is open.
 */
function isTypingTarget(eventTarget: EventTarget | null): boolean {
  const target = eventTarget instanceof HTMLElement ? eventTarget : null;
  if (!target) return false;
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
    return true;
  }
  if (target.isContentEditable) return true;
  return Boolean(target.closest('[role="listbox"], [role="menu"], [role="dialog"], [role="combobox"]'));
}

/** A change set only carries changes when it is not a whole-document decision. */
function changesOf(item: RemediationReviewItem | null): AnyChange[] {
  const changeSet = item?.changeSet;
  if (!changeSet || changeSet.mode === 'WHOLE_DOCUMENT') return [];
  return changeSet.changes;
}

export interface ReviewCursor {
  /** The open document. Falls back to the first reviewable one. */
  item: RemediationReviewItem | null;
  changes: AnyChange[];
  /** Prose changes only — both document panes key their matching off this. */
  proseChanges: ProseChange[];
  selectedId: string | null;
  /** The open document's id, or null before anything has landed. */
  openItemId: string | null;
  /** Change ids in document order — what the rail pages through. */
  order: string[];
  /** Whether the overlay change rail is showing, on a screen too narrow to pin it. */
  railOpen: boolean;
  setRailOpen: (open: boolean) => void;
  openItem: (itemId: string) => void;
  /** Picking a change in the document. On a narrow screen the rail follows. */
  selectChange: (changeId: string | null) => void;
  /** Picking a change in the rail itself. On a narrow screen the rail stands aside. */
  selectFromRail: (changeId: string | null) => void;
  decideChange: (changeId: string, decision: ReviewDecision) => void;
  navChange: (direction: 'prev' | 'next') => void;
}

/**
 * Which document is open, which change is focused, and how both move.
 *
 * REVIEWING IS A QUEUE, NOT A BROWSE. The reviewer's question after accepting
 * change 3 is always "what about change 4", so the cursor advances on every
 * decision and the A/R/arrow shortcuts keep the whole screen usable from the
 * keyboard. That only works if the cursor moves immediately rather than waiting
 * for the refetch — a reviewer holding A should walk the document, not re-decide
 * the same change.
 *
 * THE OPEN DOCUMENT IS HELD BY ID, NOT BY POSITION. Deciding a change refetches
 * the run, and the reviewable list is a filtered view of one that can still be
 * growing. An index into it points at a different document as soon as an earlier
 * one settles, which moved the reviewer off the document they were working on.
 */
export function useReviewCursor(input: {
  /** Reviewable documents, in display order. */
  items: RemediationReviewItem[];
  /** `?item=` from the run screen — opens that document instead of the first. */
  focusItemId: string | null;
  /** Whether the keyboard shortcuts are live. */
  keyboardEnabled: boolean;
  /** Narrow screens surface the change rail as an overlay rather than pinning it. */
  compactRail: boolean;
  /** Submits one decision. The cursor advances regardless of when it lands. */
  submitDecision: (itemId: string, changeId: string, decision: ReviewDecision) => void;
}): ReviewCursor {
  const { items, focusItemId, keyboardEnabled, compactRail, submitDecision } = input;

  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [railOpen, setRailOpen] = useState(false);

  const item = useMemo(
    () => items.find((candidate) => candidate.itemId === activeItemId) ?? items[0] ?? null,
    [items, activeItemId],
  );
  const changes = useMemo(() => changesOf(item), [item]);
  const order = useMemo(() => changes.map((change) => change.changeId), [changes]);
  // Memoized, not filtered inline: both document panes key work off this array,
  // and a fresh one on every render would re-run their matching each time.
  const proseChanges = useMemo(
    () => changes.filter((change): change is ProseChange => 'blockId' in change),
    [changes],
  );

  const openItemId = item?.itemId ?? null;

  // Applied once, when the id first resolves against a document that has landed.
  // This effect re-runs on every refetch, and re-applying a query param the
  // reviewer has since navigated away from would drag them back to it each time
  // they decided a change.
  const focusApplied = useRef(false);
  useEffect(() => {
    if (focusApplied.current || !focusItemId) return;
    if (!items.some((candidate) => candidate.itemId === focusItemId)) return;
    focusApplied.current = true;
    setActiveItemId(focusItemId);
  }, [focusItemId, items]);

  // Switching documents is a move on the canvas, so the overlay rail stands
  // aside rather than covering the document just opened.
  useEffect(() => {
    setRailOpen(false);
  }, [openItemId]);

  // Focus the first undecided change when a document is opened — the point of
  // the screen is to work through what is still outstanding.
  //
  // Keyed to the document rather than to `changes`, which is a new array after
  // every refetch: running it again on each decision pulled the reviewer back to
  // the first pending change, and on a document with nothing pending left, all
  // the way back to change one.
  const focusedDoc = useRef<string | null>(null);
  useEffect(() => {
    if (focusedDoc.current === openItemId) return;
    focusedDoc.current = openItemId;
    const firstPending = changes.find(
      (change) => change.decision === RemediationDecision.PENDING,
    );
    setSelectedId(firstPending?.changeId ?? changes[0]?.changeId ?? null);
  }, [openItemId, changes]);

  const decideChange = useCallback(
    (changeId: string, decision: ReviewDecision) => {
      if (!item) return;
      submitDecision(item.itemId, changeId, decision);

      // Null means nothing else is outstanding here, and the cursor stays put so
      // the reviewer can see what they just chose.
      const next = nextChangeAfterDecision(changes, changeId);
      if (next) setSelectedId(next);
    },
    [item, submitDecision, changes],
  );

  // Picking a change in the document is a request to decide it, so on a narrow
  // screen the overlay rail comes along — without it the tap highlights
  // something the reviewer then has no way to act on. Clearing the selection is
  // not such a request.
  const selectChange = useCallback(
    (changeId: string | null) => {
      setSelectedId(changeId);
      if (changeId && compactRail) setRailOpen(true);
    },
    [compactRail],
  );

  // As an overlay the rail covers the document it just scrolled to, so picking a
  // change there hands the canvas back.
  const selectFromRail = useCallback(
    (changeId: string | null) => {
      setSelectedId(changeId);
      if (compactRail) setRailOpen(false);
    },
    [compactRail],
  );

  const navChange = useCallback(
    (direction: 'prev' | 'next') => {
      const current = selectedId ? order.indexOf(selectedId) : -1;
      const next =
        direction === 'next'
          ? Math.min(order.length - 1, current + 1)
          : Math.max(0, current - 1);
      setSelectedId(order[next] ?? null);
    },
    [order, selectedId],
  );

  // A / R decide, arrows move. Mirrors the design's shortcut set.
  useEffect(() => {
    if (!keyboardEnabled || !selectedId) return;
    const onKey = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      if (event.key === 'a' || event.key === 'A') decideChange(selectedId, 'ACCEPTED');
      else if (event.key === 'r' || event.key === 'R') decideChange(selectedId, 'REJECTED');
      else if (event.key === 'ArrowDown' || event.key === 'j') {
        event.preventDefault();
        navChange('next');
      } else if (event.key === 'ArrowUp' || event.key === 'k') {
        event.preventDefault();
        navChange('prev');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [keyboardEnabled, selectedId, decideChange, navChange]);

  return {
    item,
    changes,
    proseChanges,
    selectedId,
    openItemId,
    order,
    railOpen,
    setRailOpen,
    openItem: setActiveItemId,
    selectChange,
    selectFromRail,
    decideChange,
    navChange,
  };
}
